import clsx from "clsx";
import React from "react";
import { useTranslation } from "react-i18next";
import { AreaChart, Area, ResponsiveContainer } from "recharts";
import { TokenBadge } from "~/components/TokenBadge/TokenBadge";
import { TokenIconSize } from "~/components/TokenIcon";
import { Token, TokenCandle } from "~/types";
import {
    formatPercent,
    formatMarketCap,
    formatVolume,
} from "~/utils/number-format";

const SpotlightToken: React.FC<{
    token: { token_info: Token; candles: TokenCandle[] };
    onClick: (token: { token_info: Token; candles: TokenCandle[] }) => void;
}> = ({ token, onClick }) => {
    const { t } = useTranslation();
    const change = token.token_info.token_market.change;
    const candlesData = token.candles ?? [];
    const data = candlesData.map((item) => {
        return {
            open: item.open_usd,
            close: item.close_usd,
            ts: item.ts,
        };
    });

    return (
        <div
            className="w-[210px] border-[0.5px] border-border_2 rounded-xl py-2.5 my-0.5 px-3 relative"
            onClick={() => onClick(token)}
        >
            <div>
                <div className="flex items-center">
                    <TokenBadge
                        size={TokenIconSize.size36}
                        tailIconSize={TokenIconSize.mini}
                        icon={token.token_info.token_base.logo}
                        symbol={token.token_info.token_base.symbol}
                        tokenIconBorder={true}
                        chainIcon={token.token_info.token_base.chain_logo}
                        chainName={token.token_info.token_base.chain_name}
                    />
                    <span className="text-text_1 text-body1 font-semibold ml-2 w-[75px] text-ellipsis overflow-hidden whitespace-nowrap">
                        {token.token_info.token_base.symbol.toUpperCase()}
                    </span>
                </div>
            </div>
            <div className="mt-1">
                <div>
                    <span
                        className={clsx(
                            "font-semibold text-body1",
                            token.token_info.token_market.change == 0
                                ? "!text-text_3"
                                : token.token_info.token_market.change >= 0
                                  ? "!text-text_buy"
                                  : "!text-text_sell"
                        )}
                    >
                        {formatPercent(token.token_info.token_market.change)}
                    </span>
                </div>
                <div className="h-4 flex items-center mt-0.5">
                    <span className="text-text_2 text-direction2">
                        {t("mkt_cap")}
                    </span>
                    <span className="text-text_2 text-direction2 ml-0.5">
                        {formatMarketCap(
                            token.token_info.token_market.market_cap
                        )}
                    </span>
                    <span className="h-3 bg-border_2 w-[1px] inline-block mx-2"></span>
                    <span className="text-text_2 text-direction2">
                        {t("volume")}
                    </span>
                    <span className="text-text_2 text-direction2 ml-0.5">
                        {formatVolume(
                            token.token_info.token_market.volume_value
                        )}
                    </span>
                </div>
            </div>
            <div className="w-[100px] h-[80px] absolute -top-2.5 -right-3 border-none border-border_1 pointer-events-none scale-[0.6]">
                <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                        data={data}
                        margin={{ top: 10, right: 0, left: 0, bottom: 0 }}
                    >
                        <defs>
                            <linearGradient
                                id="colorUp"
                                x1="0"
                                y1="0"
                                x2="0"
                                y2="1"
                            >
                                <stop
                                    offset="5%"
                                    stopColor="#00dd69"
                                    stopOpacity={0.2}
                                />
                                <stop
                                    offset="95%"
                                    stopColor="#00dd69"
                                    stopOpacity={0}
                                />
                            </linearGradient>
                            <linearGradient
                                id="colorEq"
                                x1="0"
                                y1="0"
                                x2="0"
                                y2="1"
                            >
                                <stop
                                    offset="5%"
                                    stopColor="#707579"
                                    stopOpacity={0.2}
                                />
                                <stop
                                    offset="95%"
                                    stopColor="#707579"
                                    stopOpacity={0}
                                />
                            </linearGradient>
                            <linearGradient
                                id="colorDown"
                                x1="0"
                                y1="0"
                                x2="0"
                                y2="1"
                            >
                                <stop
                                    offset="5%"
                                    stopColor="#fe3b30"
                                    stopOpacity={0.2}
                                />
                                <stop
                                    offset="95%"
                                    stopColor="#fe3b30"
                                    stopOpacity={0}
                                />
                            </linearGradient>
                        </defs>
                        <Area
                            type="monotone"
                            dataKey="close"
                            stroke={
                                change > 0
                                    ? "#00DD69"
                                    : change < 0
                                      ? "#FE3B30"
                                      : "#707579"
                            }
                            strokeWidth={2}
                            fillOpacity={1}
                            fill={
                                change > 0
                                    ? "url(#colorUp)"
                                    : change < 0
                                      ? "url(#colorDown)"
                                      : "url(#colorEq)"
                            }
                            isAnimationActive={false}
                        />
                    </AreaChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};

export default SpotlightToken;
